import { SearchInputComponent } from '@shared/components/search-input/search-input.component';
import {
  IUserAttributes,
  ProfileDropdownComponent,
} from '@shared/components/profile-dropdown/profile-dropdown.component';
import { LanguageDropdownComponent } from '@shared/components/language-dropdown/language-dropdown.component';
import { ActivatedRoute, RouterLink, RouterLinkActive } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoginModalComponent } from '../../../pages/auth/login-modal/login-modal.component';
import { BUTTON_TYPES } from '@shared/components/button/button-types.enum';
import { AuthenticationService } from '../../../services/authentication.service';
import { AfterViewInit, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime, fromEvent, Subject, take } from 'rxjs';
import { IFoldersResponse, IFullFranchiseItem, IHome_FranchisesResponse } from '@shared/constants/models.const';
import { ContentService } from '../../../services/content.service';
import {
  E_CONTENT_TAG_TYPE,
  E_COUNTRY,
  E_FOLDER_TYPES,
  E_FR_LOC,
  E_HEADER_LANG,
  STORAGE_KEYS,
} from '@shared/constants/enums.const';
import { BaseComponent } from '@shared/classes/base.class';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { HelperService } from '../../../services/helper.service';
import { ResponsivityService } from '../../../services/responsivity.service';
import { GreedyHeaderService } from '@shared/components/header/greedy-header.service';
import { ClickOutside } from '@shared/directives/click-outside';
import { ButtonComponent } from '@shared/components/button/button.component';
import { FranchiseService } from '../../../services/franchise.service';

@UntilDestroy()
@Component({
  selector: 'app-header',
  imports: [
    NgOptimizedImage,
    SearchInputComponent,
    LanguageDropdownComponent,
    RouterLink,
    RouterLinkActive,
    NgClass,
    ReactiveFormsModule,
    TranslateModule,
    ClickOutside,
    ProfileDropdownComponent,
    ButtonComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent extends BaseComponent implements OnInit, AfterViewInit {
  @ViewChild('navigation') navigation!: ElementRef;
  @ViewChild('navButton') navButton!: ElementRef;
  @ViewChild('firstLink') firstLink!: ElementRef;
  @ViewChild('hiddenLinks') hiddenLinks!: ElementRef;
  @ViewChild('linksWrapper') linksWrapper!: ElementRef;

  menuDropdownIsOpen = false;
  $hasMenu = new Subject<void>();

  readonly E_COUNTRY = E_COUNTRY;
  readonly E_FR_LOC = E_FR_LOC;
  readonly E_FOLDER_TYPES = E_FOLDER_TYPES;
  readonly homeItem = { type: E_FOLDER_TYPES.Home, sourceUrl: 'home' };

  user: IUserAttributes | undefined;
  isSideMenuOpen = false;
  isSearchOpen = false;
  frItems: IFullFranchiseItem[] = [];
  menuItems: IFoldersResponse[] = [];
  franchises: IHome_FranchisesResponse[] = [];
  currentFrRoute = '';
  searchValue = new FormControl();

  readonly dialog = inject(MatDialog);
  readonly ButtonTypes = BUTTON_TYPES;
  readonly authService: AuthenticationService = inject(AuthenticationService);
  readonly route: ActivatedRoute = inject(ActivatedRoute);
  readonly HELPER: HelperService = inject(HelperService);
  readonly responsivityService: ResponsivityService = inject(ResponsivityService);
  readonly franchiseService: FranchiseService = inject(FranchiseService);
  readonly GREEDY: GreedyHeaderService = inject(GreedyHeaderService);
  readonly CONTENT: ContentService = inject(ContentService);


  private tagsCache = new Map<string, string[]>();
  private urlCache = new Map<string, string>();

  constructor() {
    super();
  }

  get isFrLogo(): boolean {
    return (this.currentFrItem
      && ((this.currentFrItem.route === E_FR_LOC.caFr)
        || (this.currentFrItem.route === E_FR_LOC.caEn)));
  }


  get rootFolderId(): Number {
    return this.HELPER.rootFolderId;
  }

  get currentFrItem(): IFullFranchiseItem {
    return this.HELPER.currentFrItem;
  }

  get frLoc(): string {
    return this.HELPER.frLoc;
  }

  get noItemsToHide(): boolean {
    return !this.GREEDY.hasItemsToHide;
  }

  onFrChange(selectedFranchise: IFullFranchiseItem) {
    if (selectedFranchise.route !== this.frLoc) {
      this.HELPER.changeFrClicked = true;
      this.HELPER.currentFrItem = selectedFranchise;
      const frUrl = this.HELPER.getUrlFromPageData(selectedFranchise.route);
      // SET lang attribute in <html>

      if (frUrl) {
        this.HELPER.goUrl(frUrl, true);
      } else {
        this.HELPER.changeFrLoc(selectedFranchise.route);
      }

      document.documentElement.setAttribute('lang', (E_HEADER_LANG as any)[selectedFranchise.route]);
      (window as any).OneTrust?.changeLanguage?.(selectedFranchise.route);
    }
  }


  openDialog() {
    const dialogRef = this.dialog.open(LoginModalComponent, { panelClass: 'custom-dialog' });
    dialogRef.afterClosed().pipe(untilDestroyed(this)).subscribe();
  }

  ngAfterViewInit(): void {
    this.$hasMenu
      .pipe(debounceTime(100))
      .subscribe(() => {
        this.initGreedyMenu();
      });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.getFrList();
    this.getMenuList();
    this.handleSearch();

    if (this.HELPER.loginEnabled) {
      this.authService.user
        .pipe(untilDestroyed(this))
        .subscribe(user => {
          this.user = user as IUserAttributes;
          const newUser = JSON.stringify(user);
          const sessionUser = sessionStorage.getItem('user');

          const userReloaded = sessionStorage.getItem('userReloaded');
          if (!userReloaded) {
            sessionStorage.setItem('userReloaded', 'true');
            setTimeout(() => window.location.reload(), 500);
          }


          if (newUser !== sessionUser) {
            sessionStorage.setItem('user', newUser);
            this.userChangeHandler();
          }


        });
    }

    this.responsivityService.isMenuOpen.pipe(untilDestroyed(this))
      .subscribe(value => this.isSideMenuOpen = value);

    this.HELPER.$onReload.subscribe(() => {
      this.getFrList();
    });


  }

  runCheckGreedyMenu(): void {
    const hasParent = !!this.linksWrapper?.nativeElement?.offsetParent;
    const frItemsCount = this.HELPER?.frItems?.length;
    const renderedMenuItemsCount: number = this.linksWrapper?.nativeElement?.childNodes?.length || 0;
    const renderedHiddenMenuItemsCount = this.hiddenLinks.nativeElement.childNodes.length;

    this.toggleMenuDropdown(true);

    if (hasParent && (frItemsCount <= (renderedMenuItemsCount + renderedHiddenMenuItemsCount))) {
      this.GREEDY.setElements(
        this.navigation,
        this.navButton,
        this.linksWrapper,
        this.hiddenLinks,
      );
      this.GREEDY.check();
    }
  }

  initGreedyMenu(): void {
    this.runCheckGreedyMenu();

    fromEvent(window, 'resize')
      .pipe(
        untilDestroyed(this),
        debounceTime(200),
      )
      .subscribe(() => {
        this.runCheckGreedyMenu();
      });
  }

  userChangeHandler(): void {
    if (!this.user) return;

    const frLoc: E_FR_LOC = this.user.attributes['custom:location'] as E_FR_LOC;
    const tagTypes = this.user.attributes['custom:preferred_content']?.split(',')
      .map((tagName: any) => E_CONTENT_TAG_TYPE[tagName]);

    if (tagTypes && tagTypes.length) {
      sessionStorage.setItem(STORAGE_KEYS.tags, JSON.stringify(tagTypes));
    }

    // if its home page or profile change frloc
    if (this.HELPER.getUrlArray().length <= 1 || this.HELPER.getUrlArray().includes('profile')) {
      this.HELPER.changeFrLoc(frLoc);
    }

  }

  handleSearch(): void {
    this.route.queryParams.pipe(untilDestroyed(this)).subscribe(params => {
      if (!this.searchValue.value || this.searchValue.value?.length <= 0 && params['prompt'].length < 0) {
        this.searchValue.setValue(params['prompt']);
      }
    });

    this.searchValue.valueChanges
      // Adding delay for at least 3 chars in search field
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        if (value >= 3) {
          this.HELPER.updateFavIco();
          this.router.navigate([`${this.frLoc}/search`], {
            queryParams: { prompt: value },
            relativeTo: this.route,
          });
        }
      });
  }

  navigateToSearch(): void {
    const allSelectedValue = this.HELPER.getAllCategoriesSum();
    if (this.searchValue.value) {
      this.router.navigate([`${this.frLoc}/search`], {
        queryParams: { prompt: this.searchValue.value, 'content-type': allSelectedValue },
        relativeTo: this.route,
      });
    }
  }

  setCurrentFranchisesItem(frItems: IHome_FranchisesResponse[]): void {
    const franchisesList: any[] = [];
    frItems.forEach(frItem => {
      const countryArr: any[] = [];
      frItem.localizations.forEach(localization => {
        const franchise = {
          ...frItem,
          ...localization,
        };

        if (franchise.route === this.frLoc) {
          this.HELPER.currentFrItem = franchise;
          this.franchiseService.setCurrentFranchise(franchise);
        }

        countryArr.push(franchise);
      });
      franchisesList.push(countryArr);
    });

    this.frItems = franchisesList;
    this.HELPER.updateFavIco();
  }

  override onReload() {
    super.onReload();
    if (this.currentFrRoute !== this.frLoc) {
      this.getMenuList(true);
    }
    this.HELPER.updateFavIco();
  }

  getFrList(): void {
    this.CONTENT.getFranchises()
      .pipe(take(1))
      .subscribe(data => {
        this.franchises = data;
        this.setCurrentFranchisesItem(data);
      });
  }

  getMenuList(forceReload = false): void {
    this.CONTENT.getMainMenu(forceReload)
      .pipe(
        take(1),
      )
      .subscribe(menu => {
        if (menu.length) {
          this.currentFrRoute = this.frLoc;
          this.menuItems = menu;
          this.$hasMenu.next();
        }
      });
  }


  toggleSideMenu() {
    this.isSideMenuOpen ? this.responsivityService.closeMenu() : this.responsivityService.openMenu();
  }

  getTags(item: IFoldersResponse | any) {
    const cacheKey = `${item.type}_${item.sourceUrl}`;
    const forTagsArr: string[] = [E_FOLDER_TYPES.News, E_FOLDER_TYPES.Home, E_FOLDER_TYPES.Publication];

    const result: any = forTagsArr.includes(item.type) ? this.HELPER.hasTags() : [];
    if (!result?.length && this.tagsCache.has(cacheKey)) {
      return this.tagsCache.get(cacheKey);
    }

    this.tagsCache.set(cacheKey, result);
    return result;

  }

  getUrl(item: IFoldersResponse | any) {
    const cacheKey = item.sourceUrl;
    const result = this.HELPER.getUrl(item.sourceUrl);
    if (!result && this.urlCache.has(cacheKey)) {
      return this.urlCache.get(cacheKey);
    }

    this.urlCache.set(cacheKey, result);
    return result;
  }

  toggleSearch(): void {
    if (this.isSearchOpen) {
      this.searchValue.setValue('');
    }
    this.isSearchOpen = !this.isSearchOpen;
  }

  toggleMenuDropdown(close = false): void {
    if (close) {
      this.menuDropdownIsOpen = false;
    } else {
      this.menuDropdownIsOpen = !this.menuDropdownIsOpen;
    }
  }


}
